import React, { useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Heading from '@tiptap/extension-heading';
import Typography from '@tiptap/extension-typography';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { Extension } from '@tiptap/core';
import './TiptapEditor.css';

// Heading extension extended to preserve `id` attributes so TOC anchor links resolve correctly.
const HeadingWithId = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      id: {
        default: null,
        parseHTML: element => element.getAttribute('id') || null,
        renderHTML: attributes => {
          if (!attributes.id) return {};
          return { id: attributes.id };
        },
      },
    };
  },
});

// Custom streaming extension to handle real-time content updates
const StreamingExtension = Extension.create({
  name: 'streaming',
  
  addStorage() {
    return {
      accumulatedContent: '',
      lastRenderedLength: 0
    };
  },
  
  addCommands() {
    const extension = this;
    
    return {
      appendStreamingContent: (content) => ({ commands, editor }) => {
        // Accumulate the content
        extension.storage.accumulatedContent += content;
        
        // Get displayable content using our helper method
        const contentToShow = getDisplayableContent(extension.storage.accumulatedContent);
        
        if (contentToShow && contentToShow.length > extension.storage.lastRenderedLength) {
          extension.storage.lastRenderedLength = contentToShow.length;
          const htmlContent = convertToDisplayHTML(contentToShow);
          return commands.setContent(htmlContent);
        }
        
        return true;
      },
      
      replaceAllContent: (content) => ({ commands }) => {
        // Clear the accumulation when replacing content
        extension.storage.accumulatedContent = '';
        extension.storage.lastRenderedLength = 0;
        
        if (!content) return commands.clearContent();
        
        // For final content replacement, clean up and parse as HTML
        let cleanContent = content;
        
        if (content.includes('<') || content.includes('>')) {
          cleanContent = content
            .replace(/```html/g, '')
            .replace(/```/g, '')
            .replace(/^\s*```\s*/gm, '')
            .trim();
        }
        
        return commands.setContent(cleanContent);
      },
      
      setStreamingMode: (isStreaming) => ({ editor }) => {
        try {
          if (!isStreaming) {
            // When streaming ends, process any remaining content
            if (extension.storage.accumulatedContent) {
              const finalContent = convertToDisplayHTML(extension.storage.accumulatedContent);
              
              // Use a timeout to ensure any pending transactions are completed
              setTimeout(() => {
                if (editor && !editor.isDestroyed) {
                  try {
                    editor.commands.setContent(finalContent);
                  } catch (error) {
                    console.warn('Error setting final content:', error);
                    // If setContent fails, try a gentler approach
                    try {
                      editor.commands.clearContent();
                      editor.commands.insertContent(finalContent);
                    } catch (fallbackError) {
                      console.warn('Fallback content setting also failed:', fallbackError);
                    }
                  }
                }
              }, 50);
            }
            
            // Reset storage
            extension.storage.accumulatedContent = '';
            extension.storage.lastRenderedLength = 0;
          } else {
            // When streaming starts, reset everything
            extension.storage.accumulatedContent = '';
            extension.storage.lastRenderedLength = 0;
          }
          
          // Visual feedback
          const editorElement = editor.view.dom.closest('.tiptap-editor-container');
          if (editorElement) {
            editorElement.classList.toggle('streaming', isStreaming);
          }
          
          return true;
        } catch (error) {
          console.error('Error in setStreamingMode:', error);
          
          // Reset storage anyway
          extension.storage.accumulatedContent = '';
          extension.storage.lastRenderedLength = 0;
          
          return false;
        }
      }
    };
  }
});

// Helper functions for the streaming extension
function getDisplayableContent(content) {
  // Be more aggressive about showing content - show after every 20 characters or at natural breaks
  if (content.length < 20) return '';
  
  // Look for natural break points
  const htmlTagBreaks = /<\/[^>]+>/g;
  const sentenceBreaks = /[.!?]\s+/g;
  const paragraphBreaks = /\n\s*\n/g;
  const wordBreaks = /\s+/g;
  
  let lastBreakIndex = 0;
  let match;
  
  // First priority: Complete HTML tags
  while ((match = htmlTagBreaks.exec(content)) !== null) {
    lastBreakIndex = match.index + match[0].length;
  }
  
  // Second priority: Complete sentences
  if (lastBreakIndex === 0) {
    htmlTagBreaks.lastIndex = 0;
    while ((match = sentenceBreaks.exec(content)) !== null) {
      lastBreakIndex = match.index + match[0].length;
    }
  }
  
  // Third priority: Paragraph breaks
  if (lastBreakIndex === 0) {
    while ((match = paragraphBreaks.exec(content)) !== null) {
      lastBreakIndex = match.index + match[0].length;
    }
  }
  
  // Fourth priority: Show content at word boundaries if we have enough content
  if (lastBreakIndex === 0 && content.length > 50) {
    let wordMatches = [...content.matchAll(wordBreaks)];
    if (wordMatches.length > 5) {
      // Take content up to the last few words
      lastBreakIndex = wordMatches[wordMatches.length - 3].index;
    }
  }
  
  // If we found a good break point, return content up to that point
  if (lastBreakIndex > 0) {
    return content.substring(0, lastBreakIndex);
  }
  
  // As a last resort, if content is very long, show most of it
  if (content.length > 100) {
    const lastSpace = content.lastIndexOf(' ', content.length - 10);
    if (lastSpace > 50) {
      return content.substring(0, lastSpace);
    }
  }
  
  return '';
}

function convertToDisplayHTML(content) {
  if (!content) return '<p></p>';
  
  // If content already contains HTML tags, clean it up but preserve structure
  if (content.includes('<') && content.includes('>')) {
    let cleaned = content
      .replace(/```html/g, '')
      .replace(/```/g, '')
      .trim();
    
    // Ensure we have some content
    if (!cleaned || cleaned === '<p></p>') {
      return '<p></p>';
    }
    
    return cleaned;
  }
  
  // Convert plain text to HTML
  if (!content.trim()) {
    return '<p></p>';
  }
  
  // Split by double newlines for paragraphs
  const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim());
  
  if (paragraphs.length === 0) {
    // Single paragraph
    return `<p>${content.trim()}</p>`;
  }
  
  return paragraphs.map(p => `<p>${p.trim()}</p>`).join('');
}

// Custom CTA Button node
const CTAButton = Extension.create({
  name: 'ctaButton',
  
  addCommands() {
    return {
      insertCTAButton: (attributes) => ({ commands }) => {
        return commands.insertContent({
          type: 'paragraph',
          content: [{
            type: 'text',
            marks: [{
              type: 'textStyle',
              attrs: {
                class: 'cta-button'
              }
            }],
            text: attributes.text || 'Click Here'
          }]
        });
      }
    };
  }
});

// Custom CTA Banner node
const CTABanner = Extension.create({
  name: 'ctaBanner',
  
  addCommands() {
    return {
      insertCTABanner: (attributes) => ({ commands }) => {
        return commands.insertContent({
          type: 'paragraph',
          attrs: {
            class: 'cta-banner'
          },
          content: [{
            type: 'text',
            text: attributes.text || 'Sign up now for more helpful blog posts'
          }]
        });
      }
    };
  }
});

const TiptapEditor = forwardRef(({ 
  initialContent = '', 
  onContentChange,
  isStreaming = false,
  onEnhancementRequest 
}, ref) => {
  
  const handleEditorClick = useCallback((e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || !href.startsWith('#')) return;
    e.preventDefault();
    const targetId = href.slice(1);
    const editorContainer = e.currentTarget;
    const targetElement = editorContainer.querySelector(`[id="${CSS.escape(targetId)}"]`);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Heading is replaced by HeadingWithId below so that id attributes are preserved.
        heading: false,
      }),
      HeadingWithId.configure({
        levels: [1, 2, 3, 4, 5, 6],
      }),
      Typography,
      TextStyle,
      FontFamily,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        // Clear defaults so anchor (#) links don't get target="_blank"
        HTMLAttributes: {
          rel: null,
          target: null,
        },
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      StreamingExtension,
      CTAButton,
      CTABanner,
    ],
    content: initialContent,
    editorProps: {
      attributes: {
        class: 'blog-editor prose prose-lg max-w-none focus:outline-none',
      },
    },
    onUpdate: ({ editor }) => {
      if (onContentChange && !isStreaming) {
        onContentChange(editor.getHTML());
      }
    },
  });

  // Expose editor methods to parent component
  useImperativeHandle(ref, () => ({
    getContent: (options = {}) => {
      if (!editor) return '';
      return options.format === 'text' ? editor.getText() : editor.getHTML();
    },
    
    setContent: (content) => {
      if (editor) {
        // Use native Tiptap setContent for reliable content setting
        try {
          editor.commands.setContent(content);
        } catch (error) {
          console.warn('Error with native setContent, trying replaceAllContent:', error);
          editor.commands.replaceAllContent(content);
        }
      }
    },
    
    appendStreamingContent: (content) => {
      if (editor) {
        editor.commands.appendStreamingContent(content);
      }
    },
    
    setStreamingMode: (isStreaming) => {
      if (editor) {
        editor.commands.setStreamingMode(isStreaming);
      }
    },
    
    clearContent: () => {
      if (editor) {
        editor.commands.clearContent();
      }
    },
    
    insertEnhancement: (type, attributes = {}) => {
      if (!editor) return;
      
      switch (type) {
        case 'orderedList':
          editor.commands.toggleOrderedList();
          break;
        case 'bulletList':
          editor.commands.toggleBulletList();
          break;
        case 'blockquote':
          editor.commands.toggleBlockquote();
          break;
        case 'ctaButton':
          editor.commands.insertCTAButton(attributes);
          break;
        case 'ctaBanner':
          editor.commands.insertCTABanner(attributes);
          break;
        case 'visual':
          editor.commands.insertContent(`
            <div class="visual-placeholder">
              <p>[Visual content will be generated here]</p>
            </div>
          `);
          break;
        case 'dataVisualization':
          editor.commands.insertContent(`
            <div class="data-viz-placeholder">
              <p>[Data visualization will be generated here]</p>
            </div>
          `);
          break;
        default:
          console.warn(`Unknown enhancement type: ${type}`);
      }
    },
    
    focus: () => {
      if (editor) {
        editor.commands.focus();
      }
    },
    
    getEditor: () => editor
  }));

  // Update content when initialContent changes
  useEffect(() => {
    if (editor && initialContent && editor.getHTML() !== initialContent) {
      editor.commands.setContent(initialContent);
    }
  }, [initialContent, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="tiptap-editor-container" onClick={handleEditorClick}>
      <EditorContent editor={editor} />
    </div>
  );
});

TiptapEditor.displayName = 'TiptapEditor';

export default TiptapEditor; 